/**
 * Self-contained audio bed for the simulation (no asset files needed):
 *  - ambient: a soft jazzy pad (slow 7th chords) + a low office "room hum"
 *    (filtered noise), looping for the whole experience.
 *  - ring: a phone ringtone cadence for the incoming call.
 *  - speak: the attacker's scripted lines via the Web Speech API (TTS).
 * Everything is gated by a single on/off switch (persisted in localStorage),
 * toggled from the start screen. All sound starts from a user gesture (Start).
 */

import type { MusicStyle } from "../scenario/types";
import { publicAsset } from "./hostConfig";

const LS_KEY = "cf_sim_sound";
/** Separate persisted toggle for the MUSIC/ambient bed only (independent of the
 *  master `enabled` sound switch, which also covers voice + SFX). Lets the
 *  learner silence the background music while keeping spoken narration + UI
 *  cues audible. */
const LS_MUSIC_KEY = "cf_sim_music";
const LS_VOICE_KEY = "cf_sim_voice";

/** Real office ambience (typing, chatter, phones) — self-hosted, looped. */
const OFFICE_URL = publicAsset("audio/office-ambience.mp3");
const OFFICE_VOL = 0.22;

/** A scary/dramatic string bed (CC-BY, Kevin MacLeod "Long Note Four") used to
 *  score "what just happened / is happening right now" Consequence beats
 *  (BadUSB payload, the tailgate hypothetical replay, the attacker's own
 *  terminal laundering the wire) so the reveal reads as a real threat instead
 *  of the same calm scene music. Callers switch to this on mount and MUST
 *  restore the scenario's own ambient (url/volume/music) on unmount — see
 *  `Consequence.tsx`. Exported so it can be referenced without duplicating
 *  the path string in course UI code. */
export const DRAMATIC_URL = publicAsset("audio/dramatic-tension.mp3");
const DRAMATIC_VOL = 0.32;

/** Real recorded mouse click (CC0, bigsoundbank #1735). Decoded once into a
 *  buffer for instant, overlap-safe playback on every UI click. */
const CLICK_URL = publicAsset("audio/click.mp3");
let clickBuffer: AudioBuffer | null = null;
let clickStart = 0; // seconds of leading silence to skip (snappier click)
let clickLoading = false;

/**
 * Generic preloaded-sample registry (the "sfx()" mechanism referenced in the
 * acoustics plan) — same shape as the click buffer above, generalised so new
 * courses can register a real recorded sound for an action instead of a
 * synthesized tone by adding one entry here. Each entry loads once (on the
 * Start gesture, via `resume()`), decodes into an AudioBuffer, and trims
 * leading silence exactly like the click sample. Until decoded (or if the
 * fetch fails), callers fall back to their existing synth implementation —
 * zero regression risk, same contract as `click()`/`clickSynth()`.
 */
interface SampleEntry {
  url: string;
  buf: AudioBuffer | null;
  start: number;
  loading: boolean;
}
const SAMPLES: Record<string, SampleEntry> = {
  badgeBeep: { url: publicAsset("audio/badge-beep-real.mp3"), buf: null, start: 0, loading: false },
  doorOpen: { url: publicAsset("audio/door-open-boosted.mp3"), buf: null, start: 0, loading: false },
  doorClose: { url: publicAsset("audio/door-latch-boosted.mp3"), buf: null, start: 0, loading: false },
  typingKeys: { url: publicAsset("audio/typing-keys-boosted.mp3"), buf: null, start: 0, loading: false },
  usbInsert: { url: publicAsset("audio/usb-insert-boosted.mp3"), buf: null, start: 0, loading: false },
  footsteps: { url: publicAsset("audio/footsteps-boosted.mp3"), buf: null, start: 0, loading: false },
  printerCycle: { url: publicAsset("audio/printer-cycle-boosted.mp3"), buf: null, start: 0, loading: false },
  paperShuffle: { url: publicAsset("audio/paper-shuffle-boosted.mp3"), buf: null, start: 0, loading: false },
  notification: { url: publicAsset("audio/notif-boosted.mp3"), buf: null, start: 0, loading: false },
  downloadChime: { url: publicAsset("audio/dlchime-boosted.mp3"), buf: null, start: 0, loading: false },
  errorBuzz: { url: publicAsset("audio/errbuzz-boosted.mp3"), buf: null, start: 0, loading: false },
  phoneRing: { url: publicAsset("audio/ring-boosted.mp3"), buf: null, start: 0, loading: false },
  /** Bright ascending 3-note bell (real rendered mp3, not a raw synth tone) for
   *  "that decision was correct" feedback — see chimeGood()/chimeBad() below. */
  chimeGood: { url: publicAsset("audio/chime-correct.mp3"), buf: null, start: 0, loading: false },
  /** Soft, short, lowpass-filtered descending two-tone for "not quite right"
   *  feedback. Deliberately gentler than errorBuzz() (which stays a harsher
   *  square-wave rasp reserved for genuinely dangerous/blocked actions
   *  elsewhere) — this is for a graded decision, not a hard stop. */
  chimeBad: { url: publicAsset("audio/chime-wrong.mp3"), buf: null, start: 0, loading: false },
  packageUnwrap: { url: publicAsset("audio/paper-unwrap-boosted.mp3"), buf: null, start: 0, loading: false },
};

/** Short UI sounds for app and phone events (CC0, Kenney "Interface Sounds",
 *  public/audio/ui/, see CREDITS.md). Played with `audio.ui(name)`; opt-in,
 *  so courses that never call it sound exactly as before. */
export const UI_SOUNDS = {
  appOpen: "app-open",
  pageOpen: "page-open",
  alert: "alert",
  message: "message",
  success: "success",
  error: "error",
  reminder: "reminder",
  key: "key",
  docOpen: "doc-open",
} as const;
export type UiSound = keyof typeof UI_SOUNDS;
for (const [name, file] of Object.entries(UI_SOUNDS)) {
  SAMPLES[`ui_${name}`] = { url: publicAsset(`audio/ui/${file}.mp3`), buf: null, start: 0, loading: false };
}

function loadSample(key: string) {
  const entry = SAMPLES[key];
  if (!entry || entry.buf || entry.loading || !ctx) return;
  entry.loading = true;
  fetch(entry.url)
    .then((r) => r.arrayBuffer())
    .then((ab) => ctx!.decodeAudioData(ab))
    .then((buf) => {
      entry.buf = buf;
      const d = buf.getChannelData(0);
      let i = 0;
      for (; i < d.length; i++) if (Math.abs(d[i]) > 0.02) break;
      entry.start = Math.max(0, i / buf.sampleRate - 0.002);
    })
    .catch(() => {})
    .finally(() => {
      entry.loading = false;
    });
}

/** Plays a registered real sample at `gain`; returns false if not decoded yet
 *  (caller should fall back to its synth version and keep loading in the bg). */
function playSample(key: string, gain: number): boolean {
  const entry = SAMPLES[key];
  if (!entry?.buf || !ctx || !master) return false;
  const src = ctx.createBufferSource();
  src.buffer = entry.buf;
  const g = ctx.createGain();
  g.gain.value = gain;
  src.connect(g).connect(master);
  src.start(ctx.currentTime, entry.start);
  return true;
}

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let enabled = readPref();
/** MUSIC/ambient bed on/off (independent of `enabled`). */
let musicEnabled = readMusicPref();
/** Narration on/off. Music and effects stay on setMusicEnabled / setEnabled. */
let voiceEnabled = readVoicePref();
/** Live gain node of the current synthesized music bed, so the music toggle can
 *  mute/unmute it without a full restart. Set in startAmbient, cleared on stop. */
let ambientBed: GainNode | null = null;

let ambientNodes: { stop: () => void } | null = null;
let ringNodes: { stop: () => void } | null = null;
let exfilNodes: { stop: () => void } | null = null;
let ransomNodes: { stop: () => void } | null = null;
let officeEl: HTMLAudioElement | null = null;
/** Looping footstep source for courses that set `walkSteps`. */
let walkSrc: AudioBufferSourceNode | null = null;

function stopWalkLoop() {
  if (!walkSrc) return;
  try {
    walkSrc.stop();
  } catch {
    /* already stopped */
  }
  walkSrc = null;
}
let speechEl: HTMLAudioElement | null = null;
/** Every scripted clip still alive. stopSpeech kills the whole set, not
 *  only `speechEl` — a safety timer used to drop that pointer while the
 *  clip kept playing, so the next line started a second voice. */
const liveSpeech = new Set<HTMLAudioElement>();
/** Monotonic id of the latest speak() request; stale playbacks self-cancel. */
let speechToken = 0;
/** Monotonic id of the latest startAmbient/stopAmbient call. A deferred timer
 *  (the post-opening `fadeOfficeTo` call, or its own interval ticks) captures
 *  the generation it belongs to and no-ops if a NEWER ambient switch has
 *  since happened — otherwise a stale timer from the PREVIOUS track (e.g. the
 *  scene's piano bed) can fire mid-fade on the CURRENT track (e.g. the
 *  dramatic sting) and stomp its volume, which read as "both are audible at
 *  once" even though only one `<audio>` element was ever actually playing. */
let ambientGen = 0;
/** 0..1 multiplier applied on top of `ambientCurVol` while a scripted line is
 *  speaking, so narration is always clearly audible over the ambient bed
 *  instead of competing with it at full volume — see `duckAmbient()`. */
let duckFactor = 1;
/** 0..1 multiplier applied ON TOP OF `duckFactor`, but PERSISTENT for the
 *  rest of the scene rather than just while a line is speaking — see
 *  `duckSceneAudio()`. Used for e.g. "quiet the bar chatter + TV for good
 *  once the story moves into a quieter, more important conversation" where a
 *  transient per-line duck (which snaps back to full volume between lines)
 *  isn't enough. Survives `switchAmbient()`/environment cuts within the same
 *  scenario; a fresh page load resets it. */
let sceneDuckFactor = 1;
/** 0..1 while a video, game, or presentation is in front of the learner.
 *  0.2 is 80% quieter than the scenario's own level. Independent of
 *  sceneDuckFactor, so closing the video restores the course's own bed. */
let mediaDuckFactor = 1;
const mediaDuckReasons = new Set<string>();

/** Awareness-film audio boost. Talking-head films are mixed quiet (their own
 *  background music inflates the integrated loudness, so the presenter's VOICE
 *  sits several dB below the course's pure-speech narrator clips). We route the
 *  film <video> through a GainNode (+ a compressor acting as a limiter) so the
 *  voice plays about as loud as the narrator. The film is SPEECH, so it follows
 *  the Voice toggle (and the master sound toggle via `master`). Opt-in per film
 *  via audio.attachFilm(el, gainDb); other courses are unaffected. */
let filmGainDb = 6;
let filmGainNode: GainNode | null = null;
let filmComp: DynamicsCompressorNode | null = null;
const filmSources = new WeakMap<HTMLMediaElement, MediaElementAudioSourceNode>();
function applyFilmGain() {
  if (!filmGainNode || !ctx) return;
  const target = enabled && voiceEnabled ? Math.pow(10, filmGainDb / 20) : 0.0001;
  filmGainNode.gain.setTargetAtTime(target, ctx.currentTime, 0.05);
}
/** Sinks that want to know about `sceneDuckFactor` changes — e.g.
 *  `VideoScreen.tsx` registers itself here (via `registerSceneDuckSink`) so
 *  background TV screens duck together with the ambient bed, without this
 *  low-level audio module importing a react-three component directly. */
const sceneDuckSinks: Array<(factor: number) => void> = [];
/** Register a listener invoked with the current `sceneDuckFactor` whenever
 *  `duckSceneAudio()` changes it. Returns an unregister fn. */
export function registerSceneDuckSink(fn: (factor: number) => void): () => void {
  sceneDuckSinks.push(fn);
  return () => {
    const i = sceneDuckSinks.indexOf(fn);
    if (i >= 0) sceneDuckSinks.splice(i, 1);
  };
}
/** True while a scripted TTS line is fetching/playing (gates Continue buttons). */
let speaking = false;
const speakingListeners = new Set<(v: boolean) => void>();
function setSpeaking(v: boolean) {
  if (speaking === v) return;
  speaking = v;
  speakingListeners.forEach((fn) => {
    try {
      fn(v);
    } catch {
      /* ignore */
    }
  });
}

/** Current spoken line, for the global accessibility captions bar. Every
 *  speak() call sets this — independent of whether sound is actually on, so
 *  a hearing-impaired learner always gets the text regardless of the audio
 *  toggle. Cleared when the line ends or is superseded. */
let caption: string | null = null;
let captionToken = 0;
let captionStarted = 0;
let captionReadMs = 0;
const captionListeners = new Set<(text: string | null) => void>();
function setCaption(text: string | null) {
  if (caption === text) return;
  caption = text;
  if (text) {
    captionStarted = performance.now();
    captionReadMs = 1200 + text.length * 62;
  }
  captionListeners.forEach((fn) => {
    try {
      fn(text);
    } catch {
      /* ignore */
    }
  });
}
/** configured (peak) ambience volume (per-scenario; beach is quieter than office). */
let ambientVol = OFFICE_VOL;
/** live ambience volume after the start->quiet fade (so toggles restore the right level). */
let ambientCurVol = OFFICE_VOL;
let ambientFadeTimer: number | undefined;
let autoUnlockInstalled = false;

/** Single place that actually writes `officeEl.volume`, so every caller
 *  (the fade tween, ducking, the enable/mute toggles, startAmbient itself)
 *  composes the same two factors instead of racing to set a raw number:
 *  `ambientCurVol` (the fade-settled reference level) x `duckFactor`
 *  (temporarily lowered while a scripted line is speaking). */
function applyOfficeVolume() {
  if (!officeEl) return;
  officeEl.volume = enabled && musicEnabled
    ? Math.max(0, Math.min(1, ambientCurVol * duckFactor * sceneDuckFactor * mediaDuckFactor))
    : 0;
}

/** Tween the looped ambience element's REFERENCE volume (`ambientCurVol`) to
 *  `target` over `ms` (HTMLAudio has no AudioParam, so we step it). Used to
 *  drop the bed to ~45% after the opening so it never drowns narration.
 *  `gen` pins this tween to the ambient generation it was started for; if a
 *  newer switchAmbient()/stopAmbient() has since run, every tick is a no-op
 *  instead of stomping the volume of whatever is playing NOW. */
function fadeOfficeTo(target: number, ms: number, gen?: number) {
  if (typeof window !== "undefined") window.clearInterval(ambientFadeTimer);
  const startV = ambientCurVol;
  const t0 = performance.now();
  ambientFadeTimer = window.setInterval(() => {
    if (typeof gen === "number" && gen !== ambientGen) {
      if (typeof window !== "undefined") window.clearInterval(ambientFadeTimer);
      return;
    }
    const k = Math.min(1, (performance.now() - t0) / ms);
    ambientCurVol = startV + (target - startV) * k;
    applyOfficeVolume();
    if (k >= 1 && typeof window !== "undefined") window.clearInterval(ambientFadeTimer);
  }, 60);
}

/** Temporarily duck the ambient bed (recording + synth pad) while a scripted
 *  line is speaking, then restore it — so voice narration is always clearly
 *  audible over the scene/dramatic music instead of fighting it at full
 *  volume. Called from `speak()`; safe to call redundantly. */
function duckAmbient(active: boolean) {
  duckFactor = active ? 0.32 : 1;
  applyOfficeVolume();
  if (ambientBed && ctx) {
    ambientBed.gain.cancelScheduledValues(ctx.currentTime);
    ambientBed.gain.linearRampToValueAtTime(
      (musicEnabled ? 1 : 0) * duckFactor * sceneDuckFactor * mediaDuckFactor,
      ctx.currentTime + 0.22
    );
  }
}

/** Persistently lower (or restore) ambient bed + music-pad + every currently
 *  registered TV/video screen for the REST of the scene — unlike `duckAmbient`
 *  this does not snap back between narration lines. Used e.g. once the story
 *  moves from ambient bar chatter into a conversation the learner needs to
 *  actually hear clearly. `factor` is 0..1 (1 = full volume / no ducking);
 *  persists across `switchAmbient()`/environment cuts until explicitly reset. */
export function duckSceneAudio(factor: number) {
  sceneDuckFactor = Math.max(0, Math.min(1, factor));
  applyOfficeVolume();
  if (ambientBed && ctx) {
    ambientBed.gain.cancelScheduledValues(ctx.currentTime);
    ambientBed.gain.linearRampToValueAtTime(
      (musicEnabled ? 1 : 0) * duckFactor * sceneDuckFactor * mediaDuckFactor,
      ctx.currentTime + 0.6
    );
  }
  sceneDuckSinks.forEach((fn) => fn(sceneDuckFactor));
}


/** Drop the music bed while a video, game, or presentation is active.
 *  Reasons stack: the bed stays quiet until every reason is released,
 *  then it returns to the scenario's own level (including any scene duck). */
function applyMediaDuckLevel() {
  mediaDuckFactor = mediaDuckReasons.size > 0 ? 0.2 : 1;
  applyOfficeVolume();
  if (ambientBed && ctx) {
    ambientBed.gain.cancelScheduledValues(ctx.currentTime);
    ambientBed.gain.linearRampToValueAtTime(
      (musicEnabled ? 1 : 0) * duckFactor * sceneDuckFactor * mediaDuckFactor,
      ctx.currentTime + 0.28,
    );
  }
}

export function holdMediaDuckReason(reason: string) {
  mediaDuckReasons.add(reason);
  applyMediaDuckLevel();
}

export function releaseMediaDuckReason(reason: string) {
  if (!mediaDuckReasons.delete(reason)) return;
  applyMediaDuckLevel();
}

const foregroundVideos = new Set<HTMLVideoElement>();

function videoCountsAsContent(v: HTMLVideoElement): boolean {
  if (v.muted || v.volume < 0.05) return false;
  if (v.closest(".bbf-film, [role='dialog']")) return true;
  if (v.loop) return false;
  const r = v.getBoundingClientRect();
  return r.width > 200 && r.height > 120;
}

function syncVideoDuck() {
  if (foregroundVideos.size > 0) holdMediaDuckReason("video");
  else releaseMediaDuckReason("video");
}

const videoWatch = new WeakSet<HTMLVideoElement>();

/** Closing a film modal removes the <video> without a pause event, so a
 *  document-level pause listener never restores the bed. Follow the element
 *  until it leaves the document. */
function watchForegroundVideo(v: HTMLVideoElement) {
  if (videoWatch.has(v)) return;
  videoWatch.add(v);
  const tick = () => {
    // Keep following the element even if a pause event already dropped it
    // from the set. A film modal unmounts the <video> without firing pause,
    // and a startup pause must not kill this loop.
    if (!v.isConnected || v.ended) {
      videoWatch.delete(v);
      if (foregroundVideos.delete(v)) syncVideoDuck();
      return;
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

function onForegroundVideoPlay(ev: Event) {
  const v = ev.target;
  if (!(v instanceof HTMLVideoElement) || !videoCountsAsContent(v)) return;
  foregroundVideos.add(v);
  syncVideoDuck();
  watchForegroundVideo(v);
}

function onForegroundVideoStop(ev: Event) {
  const v = ev.target;
  if (!(v instanceof HTMLVideoElement)) return;
  if (!foregroundVideos.delete(v)) return;
  syncVideoDuck();
}

if (typeof document !== "undefined") {
  document.addEventListener("play", onForegroundVideoPlay, true);
  document.addEventListener("pause", onForegroundVideoStop, true);
  document.addEventListener("ended", onForegroundVideoStop, true);
  document.addEventListener("emptied", onForegroundVideoStop, true);
}

/** Resolve the looped recording url: "" -> none (music only), undefined ->
 *  office default, otherwise the given url. */
function resolveAmbientSrc(url?: string): string | null {
  if (url === "") return null;
  if (!url) return OFFICE_URL;
  // Scenario data uses root-absolute "/audio/..." paths; rebase them under the
  // build base so they resolve under /cdn/sim/ in Cyberfrog hosted mode.
  return url.startsWith("/audio/") ? publicAsset(url) : url;
}

/**
 * Synthesized music bed, per style, connected under `dest`. Returns a stop fn.
 *   office -> the original soft jazzy 7th-chord pad.
 *   cafe   -> light, relaxing major-key celesta arpeggio + occasional birdsong.
 *   home   -> warm, slow sine pad (quiet living-room calm).
 *   dark   -> a low, tense drone (detuned saws, lowpassed) for the hacker room.
 *   none   -> silence.
 */
function buildMusic(c: AudioContext, dest: AudioNode, style: MusicStyle): () => void {
  if (style === "none") return () => {};
  let stopped = false;
  const timers: number[] = [];

  if (style === "dark") {
    const g = c.createGain();
    g.gain.value = 0.0;
    g.gain.linearRampToValueAtTime(0.05, c.currentTime + 3);
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 420;
    g.connect(lp).connect(dest);
    const freqs = [55, 55 * 1.5, 82.4]; // A1 + fifth + E2, slow + ominous
    const oscs = freqs.map((f, i) => {
      const o = c.createOscillator();
      o.type = "sawtooth";
      o.frequency.value = f * (1 + (i - 1) * 0.004); // slight detune
      o.connect(g);
      o.start();
      return o;
    });
    // slow filter sweep so the drone "breathes"
    const sweep = () => {
      if (stopped) return;
      const now = c.currentTime;
      lp.frequency.cancelScheduledValues(now);
      lp.frequency.linearRampToValueAtTime(620, now + 6);
      lp.frequency.linearRampToValueAtTime(360, now + 12);
    };
    sweep();
    timers.push(window.setInterval(sweep, 12000));
    return () => {
      stopped = true;
      timers.forEach((t) => window.clearInterval(t));
      try {
        g.gain.linearRampToValueAtTime(0, c.currentTime + 0.6);
        oscs.forEach((o) => o.stop(c.currentTime + 0.8));
      } catch {
        /* ignore */
      }
    };
  }

  // --- TRAIN: a moving-carriage rumble (filtered noise, slow amplitude sway
  //     like the coach rocking) + a periodic rail-joint "clack-clack" pair.
  //     Clearly reads as "inside a moving train", never the station platform. ---
  if (style === "train") {
    const noiseBuf = c.createBuffer(1, c.sampleRate * 2, c.sampleRate);
    const nd = noiseBuf.getChannelData(0);
    for (let i = 0; i < nd.length; i++) nd[i] = Math.random() * 2 - 1;
    const rumbleSrc = c.createBufferSource();
    rumbleSrc.buffer = noiseBuf;
    rumbleSrc.loop = true;
    const rumbleLp = c.createBiquadFilter();
    rumbleLp.type = "lowpass";
    rumbleLp.frequency.value = 260;
    const rumbleGain = c.createGain();
    rumbleGain.gain.value = 0;
    rumbleGain.gain.linearRampToValueAtTime(0.09, c.currentTime + 2.5);
    rumbleSrc.connect(rumbleLp).connect(rumbleGain).connect(dest);
    rumbleSrc.start();
    // slow rock/sway so the rumble isn't a flat drone
    const sway = () => {
      if (stopped) return;
      const now = c.currentTime;
      rumbleGain.gain.cancelScheduledValues(now);
      rumbleGain.gain.linearRampToValueAtTime(0.065, now + 1.4);
      rumbleGain.gain.linearRampToValueAtTime(0.1, now + 2.8);
    };
    sway();
    timers.push(window.setInterval(sway, 2800));
    // rail-joint clack pair, roughly every 900ms (typical rail-length interval)
    const clack = () => {
      if (stopped || !ctx) return;
      const now = ctx.currentTime + 0.02;
      [0, 0.09].forEach((dt) => {
        const o = ctx!.createOscillator();
        o.type = "square";
        o.frequency.setValueAtTime(140, now + dt);
        o.frequency.exponentialRampToValueAtTime(60, now + dt + 0.05);
        const g = ctx!.createGain();
        g.gain.setValueAtTime(0.05, now + dt);
        g.gain.exponentialRampToValueAtTime(0.0001, now + dt + 0.09);
        o.connect(g).connect(dest);
        o.start(now + dt);
        o.stop(now + dt + 0.1);
      });
    };
    clack();
    timers.push(window.setInterval(clack, 900));
    return () => {
      stopped = true;
      timers.forEach((t) => window.clearInterval(t));
      try {
        rumbleGain.gain.linearRampToValueAtTime(0, c.currentTime + 0.6);
        rumbleSrc.stop(c.currentTime + 0.8);
      } catch {
        /* ignore */
      }
    };
  }

  // --- RADIO: an actual quiet pop/lofi loop (kick + hi-hat groove, plucked
  //     chord stabs, a walking bass and a simple melody) so it clearly reads as
  //     "a radio is playing", NOT the old sustained pad that sounded spooky. ---
  if (style === "radio") {
    const beat = 0.5; // 120 bpm
    const C3 = 130.81;
    const semi = (base: number, s: number) => base * Math.pow(2, s / 12);
    // I–V–vi–IV in C: C, G, Am, F. One chord per bar (4 beats). Triads + bass root.
    const prog = [
      { chord: [0, 4, 7], bassS: 0 }, // C
      { chord: [-1, 2, 7], bassS: -5 }, // G
      { chord: [0, 3, 9], bassS: -3 }, // Am
      { chord: [-3, 0, 5], bassS: -7 }, // F
    ];
    // one note per beat over the 4-bar loop; null = rest.
    const melody = [12, null, 16, 14, 12, null, 7, 9, 12, null, 11, 9, 5, 7, 9, null];
    let bi = 0;

    // shared noise buffer for the hi-hat
    const noiseBuf = c.createBuffer(1, c.sampleRate * 0.2, c.sampleRate);
    const nd = noiseBuf.getChannelData(0);
    for (let i = 0; i < nd.length; i++) nd[i] = Math.random() * 2 - 1;

    const tone = (freq: number, when: number, dur: number, gain: number, type: OscillatorType) => {
      if (!ctx) return;
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.value = freq;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, when);
      g.gain.linearRampToValueAtTime(gain, when + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
      o.connect(g).connect(dest);
      o.start(when);
      o.stop(when + dur + 0.02);
    };
    const kick = (when: number) => {
      if (!ctx) return;
      const o = ctx.createOscillator();
      o.type = "sine";
      o.frequency.setValueAtTime(120, when);
      o.frequency.exponentialRampToValueAtTime(45, when + 0.12);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.16, when);
      g.gain.exponentialRampToValueAtTime(0.0001, when + 0.2);
      o.connect(g).connect(dest);
      o.start(when);
      o.stop(when + 0.24);
    };
    const hat = (when: number, gain: number) => {
      if (!ctx) return;
      const s = ctx.createBufferSource();
      s.buffer = noiseBuf;
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = 7000;
      const g = ctx.createGain();
      g.gain.setValueAtTime(gain, when);
      g.gain.exponentialRampToValueAtTime(0.0001, when + 0.05);
      s.connect(hp).connect(g).connect(dest);
      s.start(when);
      s.stop(when + 0.06);
    };

    const step = () => {
      if (stopped || !ctx) return;
      const now = ctx.currentTime + 0.03;
      const bar = Math.floor(bi / 4) % prog.length;
      const inBar = bi % 4;
      const p = prog[bar];
      if (inBar === 0 || inBar === 2) kick(now);
      hat(now, 0.03);
      hat(now + beat / 2, 0.05); // offbeat swing
      if (inBar === 0 || inBar === 2) tone(semi(C3, p.bassS), now, 0.34, 0.1, "sine");
      if (inBar === 0 || inBar === 2)
        p.chord.forEach((s) => tone(semi(C3 * 4, s), now + 0.01, 0.34, 0.045, "triangle"));
      const m = melody[bi % melody.length];
      if (m != null) tone(semi(C3 * 4, m), now, 0.42, 0.05, "square");
      bi++;
    };
    step();
    timers.push(window.setInterval(step, beat * 1000));
    return () => {
      stopped = true;
      timers.forEach((t) => {
        window.clearInterval(t);
        window.clearTimeout(t);
      });
    };
  }

  // --- pitched pad styles (office / cafe / home): slow rotating chords.
  //     cafe is a WARM MELLOW JAZZ pad (no birds, no bell/harp arpeggio). ---
  const padGain = c.createGain();
  padGain.gain.value = style === "cafe" ? 0.045 : 0.035;
  const padLp = c.createBiquadFilter();
  padLp.type = "lowpass";
  padLp.frequency.value = style === "cafe" ? 1900 : 1400;
  padGain.connect(padLp).connect(dest);

  const root = style === "home" ? 174.61 : style === "cafe" ? 233.08 : 196; // F3 / Bb3 / G3
  const chords =
    style === "home"
      ? [
          [0, 4, 7, 11],
          [-2, 2, 5, 9],
          [-5, 0, 4, 7],
          [-7, -3, 2, 5],
        ]
      : style === "cafe"
      ? [
          [0, 4, 7, 11], // Bbmaj7 warmth
          [-3, 0, 4, 7], // Gm7
          [2, 5, 9, 12], // Cm9 colour
          [-5, -1, 2, 5], // F7 turnaround
        ]
      : [
          [0, 4, 7, 11],
          [-3, 0, 4, 9],
          [-5, -1, 2, 5],
          [2, 5, 9, 12],
        ];
  const chordMs = style === "home" ? 5600 : style === "cafe" ? 4600 : 5200;
  let ci = 0;
  const playChord = () => {
    if (stopped || !ctx) return;
    const now = ctx.currentTime;
    const semis = chords[ci % chords.length];
    ci++;
    semis.forEach((s) => {
      const o = ctx!.createOscillator();
      o.type = style === "cafe" ? "triangle" : "sine";
      o.frequency.value = root * Math.pow(2, s / 12);
      const g = ctx!.createGain();
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.25, now + 1.6);
      g.gain.linearRampToValueAtTime(0, now + chordMs / 1000);
      o.connect(g).connect(padGain);
      o.start(now);
      o.stop(now + chordMs / 1000 + 0.2);
    });
  };
  playChord();
  timers.push(window.setInterval(playChord, chordMs));

  return () => {
    stopped = true;
    timers.forEach((t) => {
      window.clearInterval(t);
      window.clearTimeout(t);
    });
  };
}

/** Cache of synthesized scripted lines (server TTS) -> object URL. */
// Maps voice|text -> a PROMISE (not a resolved URL). Storing the in-flight
// promise (not just the settled value) lets a prefetchSpeech() call and an
// immediately-following speak() call for the SAME line share ONE network
// request instead of racing two parallel calls to the TTS endpoint -- see
// fetchTts() below.
const ttsBlobCache = new Map<string, Promise<string>>();

// --- Pre-rendered scripted-voice manifest (build-time TTS) -----------------
// Scripted lines (every audio.speak()/prefetchSpeech() call EXCEPT the live
// two-way vishing call in LiveVishingCall.tsx) are fixed, author-known text.
// They are rendered ONCE at authoring time (ops/prerender-voice.mjs) into
// content-hashed mp3s at /var/lib/sim/assets/<course>/voice/<lang>/<hash>.mp3
// (served immutable at /cdn/sim/... on BOTH the standalone box and the
// Cyberfrog learner plane) and listed in a per-course manifest.json. At runtime
// a line is looked up by hash(voice|instructions|lang|text); a hit plays the
// static file directly, so a published course never re-synthesizes a scripted
// line via live OpenAI TTS per learner. Anything NOT in the manifest
// (unrendered content / mid-authoring iteration) falls straight through to the
// live TTS endpoint below — the manifest is an optimization, never a gate.
let voiceCourse = "";
/** EFFECTIVE content locale — the language the spoken TEXT is actually in (the
 *  course's overlay language, or "en" when it has none for the chosen UI
 *  locale). NOT the same as SpeakOpts.lang (a BCP-47 tag used only by the
 *  browser speechSynthesis fallback). Set by App on load / locale change. */
let voiceLocale = "en";
const voiceManifest = new Map<string, string>();
let voiceManifestCourse = "";
/** Resolves once the current course manifest fetch has settled. speak() waits
 *  on this so the first line of a scene uses the pre-rendered clip instead of
 *  racing the fetch and falling through to live TTS. */
let voiceManifestReady: Promise<void> = Promise.resolve();

/**
 * Deterministic, dependency-free hash of the tuple that uniquely identifies a
 * scripted line's audio. MUST stay byte-identical to the copy in
 * ops/prerender-voice.mjs (the renderer) so a runtime lookup matches the
 * pre-rendered file. FNV-1a run twice with different seeds -> 16 hex chars
 * (collision-safe for the few thousand distinct lines a course can have).
 */
function voiceHash(s: string): string {
  let h1 = 0x811c9dc5;
  let h2 = (0x811c9dc5 ^ 0x5f5e100) >>> 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = Math.imul(h2 ^ c, 0x01000193);
  }
  const hex = (n: number) => (n >>> 0).toString(16).padStart(8, "0");
  return hex(h1) + hex(h2);
}

/** Normalize a speak()/prefetchSpeech() call into the exact fields the renderer
 *  keyed on. `lang` is the effective content locale (see `voiceLocale`), never
 *  opts.lang. */
function ttsKey(text: string, opts?: SpeakOpts): {
  hash: string;
  voice: string;
  instructions: string;
  lang: string;
} {
  const voice = opts?.voice || "onyx";
  const instructions = opts?.instructions || "";
  const lang = voiceLocale || "en";
  return { hash: voiceHash(`${voice}\u0000${instructions}\u0000${lang}\u0000${text}`), voice, instructions, lang };
}

/** Build-time capture hook. When a headless capture driver sets
 *  `window.__CF_TTS_CAPTURE__`, every scripted line is RECORDED (not fetched or
 *  played) so the pre-render script can enumerate the exact (voice, instructions,
 *  lang, text) tuples straight from the real device code — no fragile per-device
 *  spec-walk. Returns true when the call was captured (and must not proceed to
 *  real playback). Inert (returns false) in every normal/production load. */
function captureLine(text: string, opts?: SpeakOpts): boolean {
  try {
    const w = window as unknown as {
      __CF_TTS_CAPTURE__?: boolean;
      __CF_TTS_CAPTURED__?: Array<{ hash: string; voice: string; instructions: string; lang: string; text: string }>;
    };
    if (!w.__CF_TTS_CAPTURE__) return false;
    if (!w.__CF_TTS_CAPTURED__) w.__CF_TTS_CAPTURED__ = [];
    // Emit the FULL ttsKey (including the runtime hash) so the renderer keys the
    // static mp3/manifest by the exact same hash the runtime will look up —
    // removing any risk of the two hash implementations drifting apart.
    const { hash, voice, instructions, lang } = ttsKey(text, opts);
    w.__CF_TTS_CAPTURED__.push({ hash, voice, instructions, lang, text });
    return true;
  } catch {
    return false;
  }
}

function readPref(): boolean {
  try {
    const v = localStorage.getItem(LS_KEY);
    return v == null ? true : v === "1";
  } catch {
    return true;
  }
}

function readMusicPref(): boolean {
  try {
    const v = localStorage.getItem(LS_MUSIC_KEY);
    return v == null ? true : v === "1";
  } catch {
    return true;
  }
}

function readVoicePref(): boolean {
  try {
    const v = localStorage.getItem(LS_VOICE_KEY);
    return v == null ? true : v === "1";
  } catch {
    return true;
  }
}

function ensureCtx() {
  if (!ctx) {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = enabled ? 1 : 0;
    master.connect(ctx.destination);
  }
  return ctx!;
}

/** Fetch + decode the recorded click once. Computes the offset of the first
 *  audible sample so we skip the MP3's leading silence/padding (no perceived lag). */
function loadClickBuffer() {
  if (clickBuffer || clickLoading || !ctx) return;
  clickLoading = true;
  fetch(CLICK_URL)
    .then((r) => r.arrayBuffer())
    .then((ab) => ctx!.decodeAudioData(ab))
    .then((buf) => {
      clickBuffer = buf;
      const d = buf.getChannelData(0);
      let i = 0;
      for (; i < d.length; i++) if (Math.abs(d[i]) > 0.02) break;
      clickStart = Math.max(0, i / buf.sampleRate - 0.002);
    })
    .catch(() => {})
    .finally(() => {
      clickLoading = false;
    });
}

/** Synthesized fallback tick (used only before the recorded sample is decoded). */
function clickSynth() {
  if (!ctx || !master) return;
  const c = ctx;
  const now = c.currentTime;
  const dur = 0.028;
  const buf = c.createBuffer(1, Math.max(1, Math.ceil(c.sampleRate * dur)), c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 3.2);
  const src = c.createBufferSource();
  src.buffer = buf;
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 2300;
  bp.Q.value = 0.7;
  const g = c.createGain();
  g.gain.value = 0.16;
  src.connect(bp).connect(g).connect(master);
  src.start(now);
  src.stop(now + dur);
}

/** Synthesized fallback for badgeBeep() — used only before the real sample decodes. */
function badgeBeepSynth() {
  if (!ctx || !master) return;
  const c = ctx;
  const now = c.currentTime;
  [[now, 1046.5], [now + 0.11, 1568.0]].forEach(([t, f]) => {
    const o = c.createOscillator();
    o.type = "square";
    o.frequency.value = f;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(0.16, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 4200;
    o.connect(g).connect(lp).connect(master!);
    o.start(t);
    o.stop(t + 0.1);
  });
}

/** Synthesized fallback for doorOpen() — used only before the real sample decodes. */
function doorOpenSynth() {
  if (!ctx || !master) return;
  const c = ctx;
  const now = c.currentTime;
  const bufLen = Math.floor(c.sampleRate * 0.5);
  const buf = c.createBuffer(1, bufLen, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < bufLen; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufLen);
  const src = c.createBufferSource();
  src.buffer = buf;
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.setValueAtTime(320, now);
  bp.frequency.linearRampToValueAtTime(900, now + 0.45);
  bp.Q.value = 4;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, now);
  g.gain.linearRampToValueAtTime(0.14, now + 0.08);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
  src.connect(bp).connect(g).connect(master);
  src.start(now);
  const o = c.createOscillator();
  o.type = "triangle";
  o.frequency.value = 140;
  const og = c.createGain();
  og.gain.setValueAtTime(0.0001, now + 0.42);
  og.gain.linearRampToValueAtTime(0.2, now + 0.44);
  og.gain.exponentialRampToValueAtTime(0.0001, now + 0.56);
  o.connect(og).connect(master);
  o.start(now + 0.42);
  o.stop(now + 0.6);
}

/** Synthesized fallback for doorClose() — used only before the real sample decodes. */
function doorCloseSynth() {
  if (!ctx || !master) return;
  const c = ctx;
  const now = c.currentTime;
  const o = c.createOscillator();
  o.type = "sine";
  o.frequency.setValueAtTime(180, now);
  o.frequency.exponentialRampToValueAtTime(60, now + 0.18);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, now);
  g.gain.linearRampToValueAtTime(0.26, now + 0.015);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 700;
  o.connect(g).connect(lp).connect(master);
  o.start(now);
  o.stop(now + 0.25);
}

/** Synthesized fallback for a single typing() burst — a handful of short
 *  bandpassed noise ticks with randomized timing/pitch, used only before the
 *  real sample decodes. */
function typingSynth(strokes: number) {
  if (!ctx || !master) return;
  const c = ctx;
  let t = c.currentTime;
  for (let i = 0; i < strokes; i++) {
    const dur = 0.02 + Math.random() * 0.01;
    const buf = c.createBuffer(1, Math.max(1, Math.ceil(c.sampleRate * dur)), c.sampleRate);
    const d = buf.getChannelData(0);
    for (let j = 0; j < d.length; j++) d[j] = (Math.random() * 2 - 1) * Math.pow(1 - j / d.length, 3);
    const src = c.createBufferSource();
    src.buffer = buf;
    const bp = c.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 1800 + Math.random() * 900;
    bp.Q.value = 1.1;
    const g = c.createGain();
    g.gain.value = 0.09 + Math.random() * 0.05;
    src.connect(bp).connect(g).connect(master);
    src.start(t);
    src.stop(t + dur);
    t += 0.075 + Math.random() * 0.09;
  }
}

/** Synthesized fallback for usbInsert() — a soft plastic "click-clunk" pair
 *  (contact make + a light thunk as it seats), used only before the real
 *  sample decodes. */
function usbInsertSynth() {
  if (!ctx || !master) return;
  const c = ctx;
  const now = c.currentTime;
  const dur1 = 0.02;
  const b1 = c.createBuffer(1, Math.ceil(c.sampleRate * dur1), c.sampleRate);
  const d1 = b1.getChannelData(0);
  for (let i = 0; i < d1.length; i++) d1[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d1.length, 2.4);
  const s1 = c.createBufferSource();
  s1.buffer = b1;
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 2600;
  bp.Q.value = 1.4;
  const g1 = c.createGain();
  g1.gain.value = 0.16;
  s1.connect(bp).connect(g1).connect(master);
  s1.start(now);
  const o = c.createOscillator();
  o.type = "sine";
  o.frequency.setValueAtTime(220, now + 0.05);
  o.frequency.exponentialRampToValueAtTime(90, now + 0.14);
  const g2 = c.createGain();
  g2.gain.setValueAtTime(0.0001, now + 0.05);
  g2.gain.linearRampToValueAtTime(0.14, now + 0.07);
  g2.gain.exponentialRampToValueAtTime(0.0001, now + 0.17);
  o.connect(g2).connect(master);
  o.start(now + 0.05);
  o.stop(now + 0.2);
}

/** Synthesized fallback for footsteps() — 3-4 soft filtered-noise thuds with
 *  randomized spacing, used only before the real sample decodes. */
function footstepsSynth(steps: number) {
  if (!ctx || !master) return;
  const c = ctx;
  let t = c.currentTime;
  for (let i = 0; i < steps; i++) {
    const dur = 0.09 + Math.random() * 0.03;
    const buf = c.createBuffer(1, Math.max(1, Math.ceil(c.sampleRate * dur)), c.sampleRate);
    const d = buf.getChannelData(0);
    for (let j = 0; j < d.length; j++) d[j] = (Math.random() * 2 - 1) * Math.pow(1 - j / d.length, 1.6);
    const src = c.createBufferSource();
    src.buffer = buf;
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 420 + Math.random() * 120;
    const g = c.createGain();
    g.gain.value = 0.1 + Math.random() * 0.03;
    src.connect(lp).connect(g).connect(master);
    src.start(t);
    src.stop(t + dur);
    t += 0.32 + Math.random() * 0.08;
  }
}

/** Synthesized fallback for printerCycle() — a low mechanical hum with a
 *  periodic head-carriage tick, used only before the real sample decodes. */
function printerCycleSynth() {
  if (!ctx || !master) return;
  const c = ctx;
  const now = c.currentTime;
  const o = c.createOscillator();
  o.type = "sawtooth";
  o.frequency.value = 95;
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 260;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, now);
  g.gain.linearRampToValueAtTime(0.05, now + 0.3);
  g.gain.setValueAtTime(0.05, now + 1.6);
  g.gain.linearRampToValueAtTime(0.0001, now + 2.0);
  o.connect(lp).connect(g).connect(master);
  o.start(now);
  o.stop(now + 2.05);
  for (let i = 0; i < 4; i++) {
    const dur = 0.02;
    const buf = c.createBuffer(1, Math.ceil(c.sampleRate * dur), c.sampleRate);
    const d = buf.getChannelData(0);
    for (let j = 0; j < d.length; j++) d[j] = (Math.random() * 2 - 1) * Math.pow(1 - j / d.length, 2);
    const src = c.createBufferSource();
    src.buffer = buf;
    const bp = c.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 1400;
    const gg = c.createGain();
    gg.gain.value = 0.06;
    src.connect(bp).connect(gg).connect(master);
    const t0 = now + 0.4 + i * 0.35;
    src.start(t0);
    src.stop(t0 + dur);
  }
}

/** Synthesized fallback for paperShuffle() — a short burst of filtered noise
 *  crinkles, used only before the real sample decodes. */
function paperShuffleSynth() {
  if (!ctx || !master) return;
  const c = ctx;
  const now = c.currentTime;
  const dur = 0.5;
  const buf = c.createBuffer(1, Math.ceil(c.sampleRate * dur), c.sampleRate);
  const d = buf.getChannelData(0);
  for (let j = 0; j < d.length; j++) {
    const env = Math.sin((j / d.length) * Math.PI);
    d[j] = (Math.random() * 2 - 1) * env;
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  const bp = c.createBiquadFilter();
  bp.type = "highpass";
  bp.frequency.value = 2200;
  const g = c.createGain();
  g.gain.value = 0.1;
  src.connect(bp).connect(g).connect(master);
  src.start(now);
  src.stop(now + dur);
}

/** Synthesized fallback for packageUnwrap() — two quick crackly noise bursts
 *  (a coarser, brighter cousin of paperShuffleSynth) approximating a hand
 *  tearing into a small package, used only before the real sample decodes. */
function packageUnwrapSynth() {
  if (!ctx || !master) return;
  const c = ctx;
  const now = c.currentTime;
  for (let i = 0; i < 2; i++) {
    const dur = 0.16;
    const buf = c.createBuffer(1, Math.ceil(c.sampleRate * dur), c.sampleRate);
    const d = buf.getChannelData(0);
    for (let j = 0; j < d.length; j++) {
      const env = Math.pow(1 - j / d.length, 1.4);
      d[j] = (Math.random() * 2 - 1) * env;
    }
    const src = c.createBufferSource();
    src.buffer = buf;
    const bp = c.createBiquadFilter();
    bp.type = "highpass";
    bp.frequency.value = 2600;
    const g = c.createGain();
    g.gain.value = 0.16;
    src.connect(bp).connect(g).connect(master);
    const t0 = now + i * 0.14;
    src.start(t0);
    src.stop(t0 + dur);
  }
}

export const audio = {
  isEnabled() {
    return enabled;
  },

  setEnabled(on: boolean) {
    enabled = on;
    applyFilmGain();
    try {
      localStorage.setItem(LS_KEY, on ? "1" : "0");
    } catch {
      /* ignore */
    }
    if (master && ctx) {
      master.gain.cancelScheduledValues(ctx.currentTime);
      master.gain.linearRampToValueAtTime(on ? 1 : 0, ctx.currentTime + 0.25);
    }
    applyOfficeVolume();
    if (officeEl) {
      if (on) officeEl.play().catch(() => {});
      else officeEl.pause();
    }
    if (!on) {
      try {
        window.speechSynthesis?.cancel();
      } catch {
        /* ignore */
      }
    }
  },

  /** Persistently duck (0..1, 1 = full volume) the ambient bed/music pad AND
   *  every registered background TV screen for the rest of the scene — see
   *  the module-level `duckSceneAudio()` for the full rationale. */
  duckScene(factor: number) {
    duckSceneAudio(factor);
  },

  /** Quiet the music bed (~78%) for a video, game, or presentation. */
  holdMediaDuck(reason: string) {
    holdMediaDuckReason(reason);
  },

  releaseMediaDuck(reason: string) {
    releaseMediaDuckReason(reason);
  },

  /** Route an awareness-film <video> through a boost gain + limiter so its
   *  voice plays about as loud as the narrator clips. Same-origin only (no CORS
   *  taint). Idempotent per element; follows the Voice + master toggles. Falls
   *  back to native element playback if the graph can't be built. */
  attachFilm(el: HTMLMediaElement, gainDb = 6) {
    try {
      const c = ensureCtx();
      if (c.state === "suspended") c.resume().catch(() => {});
      filmGainDb = gainDb;
      let src = filmSources.get(el);
      if (!src) {
        src = c.createMediaElementSource(el);
        filmSources.set(el, src);
      }
      if (!filmGainNode) {
        filmGainNode = c.createGain();
        filmComp = c.createDynamicsCompressor();
        filmComp.threshold.value = -4;
        filmComp.knee.value = 6;
        filmComp.ratio.value = 12;
        filmComp.attack.value = 0.003;
        filmComp.release.value = 0.25;
        filmGainNode.connect(filmComp);
        filmComp.connect(master ?? c.destination);
      }
      try { src.disconnect(); } catch { /* not connected yet */ }
      src.connect(filmGainNode);
      applyFilmGain();
    } catch {
      /* already connected / cross-origin -> leave native playback as-is */
    }
  },

  detachFilm(el: HTMLMediaElement) {
    const src = filmSources.get(el);
    if (src) { try { src.disconnect(); } catch { /* ignore */ } }
  },

  /** Current film GainNode value (debug / headless verification). */
  debugFilmGain() {
    return { gain: filmGainNode ? filmGainNode.gain.value : null, gainDb: filmGainDb, voiceEnabled, enabled };
  },

  /** Call from a user gesture (the Start button). Unlocks audio + starts the bed. */
  resume() {
    const c = ensureCtx();
    if (c.state === "suspended") c.resume().catch(() => {});
    loadClickBuffer();
    Object.keys(SAMPLES).forEach(loadSample);
    if (enabled && officeEl?.paused) officeEl.play().catch(() => {});
  },

  /**
   * Self-heals autoplay-blocked ambience. `startAmbient()`/`switchAmbient()` can
   * fire before any user gesture reached this page (e.g. a QA/dev `?beat=`
   * deep-link, or a scene-cut effect racing the Start click), so the browser
   * silently rejects `officeEl.play()` and the loop never starts — the learner
   * hears nothing for the rest of the course even though everything else is
   * correctly wired. Call ONCE at mount: the very next real pointer/key event
   * anywhere on the page retries ctx.resume() + officeEl.play(). Idempotent and
   * permanent (not one-shot), because a later scene's ambience can equally race
   * a gesture that already happened.
   */
  /** QA-only: inspect the live ambience element's real playback state. */
  debugState() {
    return {
      src: officeEl?.src ?? null,
      paused: officeEl?.paused ?? null,
      currentTime: officeEl?.currentTime ?? null,
      speechSrc: speechEl?.src ?? null,
      speechPaused: speechEl?.paused ?? null,
      speechCurrentTime: speechEl?.currentTime ?? null,
      speechError: speechEl?.error ? { code: speechEl.error.code, message: speechEl.error.message } : null,
      volume: officeEl?.volume ?? null,
      sceneDuck: sceneDuckFactor,
      mediaDuck: mediaDuckFactor,
      enabled,
      ctxState: ctx?.state ?? null,
    };
  },

  installAutoUnlock() {
    if (typeof document === "undefined" || autoUnlockInstalled) return;
    autoUnlockInstalled = true;
    const retry = () => {
      const c = ctx;
      if (c && c.state === "suspended") c.resume().catch(() => {});
      if (enabled && officeEl && officeEl.paused) officeEl.play().catch(() => {});
    };
    ["pointerdown", "keydown"].forEach((evt) =>
      document.addEventListener(evt, retry, { capture: true, passive: true }),
    );
  },

  /** Start the looping ambient bed. Pass a scenario ambient URL (beach/birds,
   *  office/chatter, ...) and optional volume (0..1), or omit for the office
   *  defaults. `music` picks the synthesized bed (office jazz / cafe / dark /
   *  home / none). An empty-string url means "no recording, music only". */
  startAmbient(url?: string, vol?: number, music: MusicStyle = "office") {
    const c = ensureCtx();
    ambientGen++;
    const myGen = ambientGen;
    ambientVol = typeof vol === "number" ? vol : OFFICE_VOL;
    ambientCurVol = ambientVol;
    duckFactor = 1;
    if (ambientNodes || !master) return;
    const src = resolveAmbientSrc(url);

    const bed = c.createGain();
    bed.gain.value = 0;
    bed.connect(master);
    // duckFactor was just reset to 1 above, but `sceneDuckFactor` is
    // intentionally persistent across switchAmbient()/environment cuts (see
    // its doc comment) — a fresh bed must still respect an already-active
    // scene-wide duck instead of ramping back up to full volume.
    bed.gain.linearRampToValueAtTime(musicEnabled ? duckFactor * sceneDuckFactor * mediaDuckFactor : 0, c.currentTime + 2);
    ambientBed = bed;

    // --- looped ambience recording (chatter/street, beach/birds, ...) ---
    // ALWAYS fully tear down the previous element (pause + drop its src, not
    // just reassign the reference) before creating the new one. A conditional
    // "only recreate if the src differs" here was the actual cause of two
    // tracks reading as simultaneously audible: a still-alive-but-orphaned
    // element could keep playing in the background while a stale deferred
    // fade timer (below) kept writing to whatever `officeEl` pointed to BY
    // THEN, not the element it was scheduled for.
    if (officeEl) {
      try {
        officeEl.pause();
        officeEl.src = "";
      } catch {
        /* ignore */
      }
      officeEl = null;
    }
    if (src) {
      officeEl = new Audio(src);
      officeEl.loop = true;
      officeEl.preload = "auto";
      applyOfficeVolume();
      if (enabled && musicEnabled) officeEl.play().catch(() => {});
      // Keep long-form ambience clearly audible after the opening. The old 7%
      // floor made looping music/cafe/station recordings sound as if they stopped.
      window.clearInterval(ambientFadeTimer);
      // Shortened 2026-07-13 (was 3500ms delay / 7000ms fade -- reported as
      // "voice hard to hear over background noise" in scenes where the
      // narration starts early, e.g. the cafe). The ambient bed now spends
      // less time at full volume before settling to its floor. Pinned to
      // `myGen` so a later switchAmbient()/stopAmbient() cancels this tween
      // instead of it firing on top of whatever plays next.
      window.setTimeout(() => {
        if (myGen !== ambientGen) return;
        fadeOfficeTo(ambientVol * 0.45, 3000, myGen);
      }, 1200);
    }

    const musicStop = buildMusic(c, bed, music);

    ambientNodes = {
      stop: () => {
        musicStop();
        try {
          bed.gain.linearRampToValueAtTime(0, c.currentTime + 0.6);
          if (officeEl) {
            officeEl.pause();
            officeEl.src = "";
            officeEl = null;
          }
        } catch {
          /* ignore */
        }
      },
    };
  },

  /** Cut the ambient bed to a new scene (different recording + music) without a
   *  full restart from the Start screen. Used when the course CUTS environments
   *  (restaurant -> hacker room -> living room). No-op if sound is off. */
  switchAmbient(url?: string, vol?: number, music: MusicStyle = "office") {
    if (!enabled) return;
    this.stopAmbient();
    this.startAmbient(url, vol, music);
  },

  /** Cut straight to the dramatic sting bed (see DRAMATIC_URL) for a
   *  "this is happening right now" reveal — a risky Consequence cinematic,
   *  not calm background music. Pair with `restoreSceneAmbient()` on
   *  unmount so the course's normal ambient comes back afterward. */
  switchToDramatic() {
    this.switchAmbient(DRAMATIC_URL, DRAMATIC_VOL, "none");
  },

  /** Restore whatever ambient a scenario declares (its `ambientUrl` /
   *  `ambientVolume` / `music`, e.g. this course's piano bed) — the
   *  counterpart to `switchToDramatic()`. Safe to call with an empty
   *  scenario-shaped object. */
  restoreSceneAmbient(scenario?: { ambientUrl?: string; ambientVolume?: number; music?: MusicStyle }) {
    this.switchAmbient(scenario?.ambientUrl, scenario?.ambientVolume, scenario?.music ?? "office");
  },

  stopAmbient() {
    ambientGen++;
    if (typeof window !== "undefined") window.clearInterval(ambientFadeTimer);
    ambientNodes?.stop();
    ambientNodes = null;
    ambientBed = null;
  },

  isMusicEnabled() {
    return musicEnabled;
  },

  /** Toggle ONLY the music/ambient bed (voice + SFX stay under setEnabled).
   *  Live-mutes the synthesized bed + the looped ambience recording without a
   *  restart, and persists the choice. */
  isVoiceEnabled() {
    return voiceEnabled;
  },

  /** Narration only. Turning it off stops the current line and leaves music playing. */
  setVoiceEnabled(on: boolean) {
    voiceEnabled = on;
    try {
      localStorage.setItem(LS_VOICE_KEY, on ? "1" : "0");
    } catch {
      /* ignore */
    }
    applyFilmGain();
    if (!on) {
      stopSpeech();
      captionToken++;
      setSpeaking(false);
      setCaption(null);
      duckAmbient(false);
    }
  },

  setMusicEnabled(on: boolean) {
    musicEnabled = on;
    try {
      localStorage.setItem(LS_MUSIC_KEY, on ? "1" : "0");
    } catch {
      /* ignore */
    }
    if (ambientBed && ctx) {
      ambientBed.gain.cancelScheduledValues(ctx.currentTime);
      ambientBed.gain.linearRampToValueAtTime(on ? duckFactor * sceneDuckFactor * mediaDuckFactor : 0, ctx.currentTime + 0.3);
    }
    applyOfficeVolume();
    if (officeEl) {
      if (enabled && on) officeEl.play().catch(() => {});
      else officeEl.pause();
    }
  },

  /** A camera shutter "snap" (used as the phishing page secretly grabs photos
   *  while the learner browses the menu). Synthesized, no asset. */
  shutter() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    const now = c.currentTime;
    // Two quick mechanical clicks (mirror up / shutter) = a classic SLR snap.
    [0, 0.06].forEach((t0, i) => {
      const t = now + t0;
      const dur = 0.03;
      const buf = c.createBuffer(1, Math.ceil(c.sampleRate * dur), c.sampleRate);
      const d = buf.getChannelData(0);
      for (let j = 0; j < d.length; j++) d[j] = (Math.random() * 2 - 1) * Math.pow(1 - j / d.length, 2.6);
      const srcN = c.createBufferSource();
      srcN.buffer = buf;
      const bp = c.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = i === 0 ? 3200 : 2200;
      bp.Q.value = 0.9;
      const g = c.createGain();
      g.gain.value = i === 0 ? 0.22 : 0.3;
      srcN.connect(bp).connect(g).connect(master!);
      srcN.start(t);
      srcN.stop(t + dur);
    });
  },

  /** Phone ringtone cadence (rings until stopRing). Plays even with sound on. */
  startRing() {
    const c = ensureCtx();
    if (ringNodes || !master) return;
    const ringGain = c.createGain();
    ringGain.gain.value = 0;
    ringGain.connect(master);

    const o1 = c.createOscillator();
    o1.type = "sine";
    o1.frequency.value = 440;
    const o2 = c.createOscillator();
    o2.type = "sine";
    o2.frequency.value = 480;
    o1.connect(ringGain);
    o2.connect(ringGain);
    o1.start();
    o2.start();

    let stopped = false;
    const cadence = () => {
      if (stopped || !ctx) return;
      const now = ctx.currentTime;
      // Prefer the real recorded single-ring peal (retriggered on the same
      // cadence the synth dual-tone already used); fall back to the synth
      // tone (silently, via near-zero gain below) until the sample decodes.
      if (playSample("phoneRing", 0.8)) {
        loadSample("phoneRing");
        return;
      }
      loadSample("phoneRing");
      // 2s ring on, 4s off (NA cadence), softened.
      ringGain.gain.setValueAtTime(0.0001, now);
      ringGain.gain.linearRampToValueAtTime(0.09, now + 0.1);
      ringGain.gain.setValueAtTime(0.09, now + 1.9);
      ringGain.gain.linearRampToValueAtTime(0.0001, now + 2.0);
    };
    cadence();
    const ringTimer = window.setInterval(cadence, 6000);

    ringNodes = {
      stop: () => {
        stopped = true;
        window.clearInterval(ringTimer);
        try {
          ringGain.gain.cancelScheduledValues(c.currentTime);
          ringGain.gain.linearRampToValueAtTime(0, c.currentTime + 0.1);
          o1.stop(c.currentTime + 0.2);
          o2.stop(c.currentTime + 0.2);
        } catch {
          /* ignore */
        }
      },
    };
  },

  stopRing() {
    ringNodes?.stop();
    ringNodes = null;
  },

  /** Classic phone-keypad DTMF dual-tone for a pressed key. */
  dtmf(key: string) {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    const pairs: Record<string, [number, number]> = {
      "1": [697, 1209], "2": [697, 1336], "3": [697, 1477],
      "4": [770, 1209], "5": [770, 1336], "6": [770, 1477],
      "7": [852, 1209], "8": [852, 1336], "9": [852, 1477],
      "*": [941, 1209], "0": [941, 1336], "#": [941, 1477],
    };
    const p = pairs[key];
    if (!p) return;
    const now = c.currentTime;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(0.14, now + 0.012);
    g.gain.setValueAtTime(0.14, now + 0.1);
    g.gain.linearRampToValueAtTime(0.0001, now + 0.13);
    g.connect(master);
    p.forEach((f) => {
      const o = c.createOscillator();
      o.type = "sine";
      o.frequency.value = f;
      o.connect(g);
      o.start(now);
      o.stop(now + 0.14);
    });
  },

  /** A real recorded "mouse click" (CC0 sample). Plays on any clickable element
   *  (wired globally in App). Falls back to a synth tick until the sample decodes. */
  click() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    if (!clickBuffer) {
      loadClickBuffer();
      clickSynth();
      return;
    }
    const src = c.createBufferSource();
    src.buffer = clickBuffer;
    const g = c.createGain();
    g.gain.value = 0.6;
    src.connect(g).connect(master);
    src.start(c.currentTime, clickStart);
  },

  /** A short, pleasant "device powering on" chime (ascending arpeggio + soft tail),
   *  Windows-startup-like. Synthesized, no asset. Plays when the screen turns on. */
  powerOn() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    const now = c.currentTime;
    const out = c.createGain();
    out.gain.setValueAtTime(0.0001, now);
    out.gain.linearRampToValueAtTime(0.5, now + 0.05);
    out.gain.linearRampToValueAtTime(0.0001, now + 1.9);
    out.connect(master);
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
    notes.forEach((f, i) => {
      const t = now + i * 0.12;
      const o = c.createOscillator();
      o.type = i === notes.length - 1 ? "triangle" : "sine";
      o.frequency.value = f;
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.3, t + 0.04);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.9);
      const lp = c.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 3400;
      o.connect(g).connect(lp).connect(out);
      o.start(t);
      o.stop(t + 1.0);
    });
  },

  /** A gentle Windows-logon-style welcome chime (warm 3-note swell), played
   *  when a simulated PC login succeeds. Synthesized, no asset. */
  logon() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    const now = c.currentTime;
    const out = c.createGain();
    out.gain.setValueAtTime(0.0001, now);
    out.gain.linearRampToValueAtTime(0.42, now + 0.06);
    out.gain.linearRampToValueAtTime(0.0001, now + 2.2);
    out.connect(master);
    // D5 -> A4 -> A5 with a soft fifth held under it: warm "welcome" swell.
    const notes: Array<[number, number, OscillatorType]> = [
      [587.33, 0, "sine"],
      [440.0, 0.16, "sine"],
      [880.0, 0.34, "triangle"],
      [293.66, 0.34, "sine"],
    ];
    notes.forEach(([f, dt, type]) => {
      const t = now + dt;
      const o = c.createOscillator();
      o.type = type;
      o.frequency.value = f;
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.26, t + 0.05);
      g.gain.exponentialRampToValueAtTime(0.001, t + 1.5);
      const lp = c.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 2800;
      o.connect(g).connect(lp).connect(out);
      o.start(t);
      o.stop(t + 1.6);
    });
  },

  /** A door-entry buzzer (two harsh low rasps), for the intercom ring. */
  buzz() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    const buzzGain = c.createGain();
    buzzGain.gain.value = 0;
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 900;
    buzzGain.connect(lp).connect(master);
    const o = c.createOscillator();
    o.type = "sawtooth";
    o.frequency.value = 150;
    o.connect(buzzGain);
    const now = c.currentTime;
    // two rasps: on/off/on
    [0, 0.55].forEach((t0) => {
      const t = now + t0;
      buzzGain.gain.setValueAtTime(0.0001, t);
      buzzGain.gain.linearRampToValueAtTime(0.12, t + 0.03);
      buzzGain.gain.setValueAtTime(0.12, t + 0.34);
      buzzGain.gain.linearRampToValueAtTime(0.0001, t + 0.4);
    });
    o.start(now);
    o.stop(now + 1.1);
  },

  /** Badge-reader "access granted" beep — a real recorded electronic-lock
   *  sample (CC0, see AUDIO-CREDITS.md) once decoded; a synth chirp fallback
   *  before that (same contract as click()/clickSynth()). */
  badgeBeep() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    if (playSample("badgeBeep", 0.85)) return;
    loadSample("badgeBeep");
    badgeBeepSynth();
  },

  /** A real recorded office-door open (CC0), falling back to a synthesized
   *  creak + latch clunk before the sample decodes. */
  doorOpen() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    if (playSample("doorOpen", 0.7)) return;
    loadSample("doorOpen");
    doorOpenSynth();
  },

  /** A real recorded door-latch click (CC0), falling back to a synthesized
   *  duller thud before the sample decodes. */
  doorClose() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    if (playSample("doorClose", 0.8)) return;
    loadSample("doorClose");
    doorCloseSynth();
  },

  /** A short burst of keyboard typing (real recorded mechanical-keyboard
   *  sample; falls back to a synthesized randomized-tick burst before the
   *  sample decodes). Use for "the learner/an NPC is typing" moments —
   *  password entry, chat replies, filling a form. `strokes` only affects
   *  the synth fallback length; the real sample always plays as one clip. */
  typing(strokes = 6) {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    if (playSample("typingKeys", 0.5)) return;
    loadSample("typingKeys");
    typingSynth(strokes);
  },

  /** A real recorded USB-stick insertion (CC0), falling back to a
   *  synthesized click-clunk before the sample decodes. */
  usbInsert() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    if (playSample("usbInsert", 1.1)) return;
    loadSample("usbInsert");
    usbInsertSynth();
  },

  /** A short burst of walking footsteps (real recorded hard-sole-on-floor
   *  sample; falls back to synthesized filtered thuds). Use for camera
   *  moves that represent the learner or an NPC walking a short distance —
   *  keep it brief, this is a punctuation cue, not a looped walk-cycle. */
  footsteps(steps = 4) {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    if (playSample("footsteps", 0.4)) return;
    loadSample("footsteps");
    footstepsSynth(steps);
  },

  /** Loop the footstep sample while `moving` is true, and stop it the
   *  moment movement stops. Courses opt in; a burst from `footsteps()` is
   *  unchanged. */
  walkLoop(moving: boolean) {
    if (!moving || !enabled) {
      stopWalkLoop();
      return;
    }
    if (walkSrc) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    const entry = SAMPLES.footsteps;
    if (!entry?.buf) {
      loadSample("footsteps");
      return;
    }
    const src = c.createBufferSource();
    src.buffer = entry.buf;
    src.loop = true;
    src.loopStart = entry.start;
    const g = c.createGain();
    g.gain.value = 0.34;
    src.connect(g).connect(master);
    try {
      src.start(c.currentTime, entry.start);
    } catch {
      return;
    }
    walkSrc = src;
    src.onended = () => {
      if (walkSrc === src) walkSrc = null;
    };
  },

  /** A real recorded office-printer cycle (CC0), falling back to a
   *  synthesized hum+tick. Use for "the printer nearby is running" cues. */
  printerCycle() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    if (playSample("printerCycle", 0.55)) return;
    loadSample("printerCycle");
    printerCycleSynth();
  },

  /** A real recorded paper-shuffle/page-turn (CC0), falling back to a
   *  synthesized crinkle. Use for picking up/handling printed documents. */
  paperShuffle() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    if (playSample("paperShuffle", 0.6)) return;
    loadSample("paperShuffle");
    paperShuffleSynth();
  },

  /** A real recorded quick paper-crumple (CC0-equivalent, royalty-free), falling
   *  back to a synthesized crackly tear. Use for unwrapping/opening a small
   *  package or envelope — distinct from paperShuffle() (loose document
   *  handling), this reads as tearing into wrapping. */
  packageUnwrap() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    if (playSample("packageUnwrap", 0.85)) return;
    loadSample("packageUnwrap");
    packageUnwrapSynth();
  },

  /** A short, satisfying descending-then-settling chime for "file saved /
   *  download complete" moments. Synthesized, no asset. */
  downloadChime() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    if (playSample("downloadChime", 0.75)) return;
    loadSample("downloadChime");
    const now = c.currentTime;
    const notes: Array<[number, number]> = [[0, 784], [0.1, 659.25], [0.22, 987.77]];
    notes.forEach(([dt, f]) => {
      const t = now + dt;
      const o = c.createOscillator();
      o.type = "triangle";
      o.frequency.value = f;
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.24, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0004, t + 0.5);
      const lp = c.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 3200;
      o.connect(g).connect(lp).connect(master!);
      o.start(t);
      o.stop(t + 0.55);
    });
  },

  /** A short, unambiguous "that was wrong / not allowed" error buzz — two
   *  flat low rasps. Distinct timbre from breachAlert() (which is a bigger,
   *  three-note "the breach just happened" sting): this is for a single
   *  in-the-moment invalid action (bad PIN, blocked action, wrong answer). */
  errorBuzz() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    if (playSample("errorBuzz", 0.7)) return;
    loadSample("errorBuzz");
    const now = c.currentTime;
    [0, 0.16].forEach((t0) => {
      const t = now + t0;
      const o = c.createOscillator();
      o.type = "square";
      o.frequency.value = 180;
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.13, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.13);
      const lp = c.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 900;
      o.connect(g).connect(lp).connect(master!);
      o.start(t);
      o.stop(t + 0.14);
    });
  },

  /** "That decision was right" — a bright, satisfying ascending chime. Plays
   *  the real rendered sample once loaded (see SAMPLES.chimeGood); the synth
   *  fallback mirrors it closely (sine triad, soft attack, quick decay) so
   *  there's no audible gap before the sample finishes loading. Use this
   *  (not the generic click()) anywhere a learner's specific choice is
   *  being graded correct in the moment, e.g. inbox-triage verdicts. */
  chimeGood() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    if (playSample("chimeGood", 0.8)) return;
    loadSample("chimeGood");
    const now = c.currentTime;
    const notes: Array<[number, number, number]> = [
      [0, 1046.5, 0.5],
      [0.09, 1318.5, 0.5],
      [0.18, 1568, 0.55],
    ];
    notes.forEach(([dt, f, vol]) => {
      const t = now + dt;
      const o = c.createOscillator();
      o.type = "sine";
      o.frequency.value = f;
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(vol * 0.5, t + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0004, t + 0.42);
      o.connect(g).connect(master!);
      o.start(t);
      o.stop(t + 0.45);
    });
  },

  /** "That decision wasn't the safest one" — a soft, short, warm descending
   *  two-tone. Deliberately gentle (lowpass-filtered sine, not a harsh
   *  square-wave rasp) so it reads as "not quite" rather than an alarm.
   *  Distinct from errorBuzz(), which stays reserved for genuinely
   *  dangerous/blocked actions elsewhere in the app. */
  chimeBad() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    if (playSample("chimeBad", 0.75)) return;
    loadSample("chimeBad");
    const now = c.currentTime;
    [[0, 392], [0.13, 311]].forEach(([dt, f]) => {
      const t = now + dt;
      const o = c.createOscillator();
      o.type = "sine";
      o.frequency.value = f;
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.22, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0003, t + 0.3);
      const lp = c.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 1700;
      o.connect(g).connect(lp).connect(master!);
      o.start(t);
      o.stop(t + 0.32);
    });
  },

  /** A short UI sound from UI_SOUNDS (app opens, page opens, phone alert,
   *  text message, success, error, reminder, keypad). Silent until decoded. */
  ui(name: UiSound, gain = 0.7) {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    const key = `ui_${name}`;
    if (playSample(key, gain)) return;
    loadSample(key);
  },

  /** A soft two-note notification ping (mail arrival, task complete, etc). */
  notification() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    if (playSample("notification", 0.7)) return;
    loadSample("notification");
    const now = c.currentTime;
    [[now, 987.77], [now + 0.09, 1318.5]].forEach(([t, f]) => {
      const o = c.createOscillator();
      o.type = "sine";
      o.frequency.value = f as number;
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, t as number);
      g.gain.linearRampToValueAtTime(0.22, (t as number) + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, (t as number) + 0.35);
      o.connect(g).connect(master!);
      o.start(t as number);
      o.stop((t as number) + 0.4);
    });
  },

  /** Short, urgent digital "packet sent" blip -- played once per file as an
   *  exfil/data-theft cinematic (ChargeExfil) lists stolen files streaming
   *  out, so the copy list reads as an active data transfer, not silence. */
  dataBlip() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    const now = c.currentTime;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(0.13, now + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.085);
    g.connect(master);
    const o = c.createOscillator();
    o.type = "square";
    o.frequency.setValueAtTime(1500, now);
    o.frequency.exponentialRampToValueAtTime(2500, now + 0.06);
    o.connect(g);
    o.start(now);
    o.stop(now + 0.09);
  },

  /** Low, pulsing "actively under attack" bed -- starts when a data-theft
   *  cinematic mounts (ChargeExfil), stops when it's dismissed. Layers on
   *  top of the scene's ambient music rather than replacing it; distinct
   *  timbre (throbbing low sawtooth) reads as tension/urgency, not music. */
  startExfilAlarm() {
    if (!enabled) return;
    const c = ensureCtx();
    if (exfilNodes || !master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    const g = c.createGain();
    g.gain.value = 0;
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 720;
    g.connect(lp).connect(master);
    const o1 = c.createOscillator();
    o1.type = "sawtooth";
    o1.frequency.value = 92;
    const o2 = c.createOscillator();
    o2.type = "sawtooth";
    o2.frequency.value = 92 * 1.5;
    o1.connect(g);
    o2.connect(g);
    o1.start();
    o2.start();
    let stopped = false;
    const pulse = () => {
      if (stopped || !ctx) return;
      const now = ctx.currentTime;
      g.gain.cancelScheduledValues(now);
      g.gain.setValueAtTime(0.0001, now);
      g.gain.linearRampToValueAtTime(0.055, now + 0.15);
      g.gain.linearRampToValueAtTime(0.0001, now + 0.6);
    };
    pulse();
    const timer = window.setInterval(pulse, 700);
    exfilNodes = {
      stop: () => {
        stopped = true;
        window.clearInterval(timer);
        try {
          g.gain.cancelScheduledValues(c.currentTime);
          g.gain.linearRampToValueAtTime(0, c.currentTime + 0.3);
          o1.stop(c.currentTime + 0.4);
          o2.stop(c.currentTime + 0.4);
        } catch {
          /* ignore */
        }
      },
    };
  },

  stopExfilAlarm() {
    exfilNodes?.stop();
    exfilNodes = null;
  },

  /** Heavier, more DRAMATIC "the whole network is falling" bed for the
   *  ransomware takeover cinematic. Deeper than startExfilAlarm: a detuned
   *  sub-bass drone + a slow dissonant two-tone air-raid siren sweeping over
   *  the top + a faster low pulse, all lowpassed so it reads as dread/alarm,
   *  not music. Layers over the scene's ambient bed; stop with
   *  stopRansomAlarm(). Purely synthesized (no downloaded asset needed) so it
   *  always plays regardless of network/CDN. */
  startRansomAlarm() {
    if (!enabled) return;
    const c = ensureCtx();
    if (ransomNodes || !master) return;
    if (c.state === "suspended") c.resume().catch(() => {});

    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 900;
    const bus = c.createGain();
    bus.gain.value = 0.9;
    bus.connect(lp).connect(master);

    // sub-bass drone (two detuned saws) — constant dread
    const drone = c.createGain();
    drone.gain.value = 0.05;
    drone.connect(bus);
    const d1 = c.createOscillator();
    d1.type = "sawtooth";
    d1.frequency.value = 46;
    const d2 = c.createOscillator();
    d2.type = "sawtooth";
    d2.frequency.value = 46 * 1.01;
    d1.connect(drone);
    d2.connect(drone);
    d1.start();
    d2.start();

    // slow air-raid siren sweeping 300->560->300 Hz
    const siren = c.createGain();
    siren.gain.value = 0.028;
    siren.connect(bus);
    const so = c.createOscillator();
    so.type = "triangle";
    so.frequency.value = 320;
    so.connect(siren);
    so.start();

    let stopped = false;
    const sweep = () => {
      if (stopped || !ctx) return;
      const now = ctx.currentTime;
      so.frequency.cancelScheduledValues(now);
      so.frequency.setValueAtTime(300, now);
      so.frequency.linearRampToValueAtTime(560, now + 1.1);
      so.frequency.linearRampToValueAtTime(300, now + 2.2);
    };
    sweep();
    const sweepTimer = window.setInterval(sweep, 2200);

    // faster low pulse for urgency
    const pg = c.createGain();
    pg.gain.value = 0;
    pg.connect(bus);
    const po = c.createOscillator();
    po.type = "square";
    po.frequency.value = 68;
    po.connect(pg);
    po.start();
    const pulse = () => {
      if (stopped || !ctx) return;
      const now = ctx.currentTime;
      pg.gain.cancelScheduledValues(now);
      pg.gain.setValueAtTime(0.0001, now);
      pg.gain.linearRampToValueAtTime(0.05, now + 0.08);
      pg.gain.linearRampToValueAtTime(0.0001, now + 0.4);
    };
    pulse();
    const pulseTimer = window.setInterval(pulse, 520);

    ransomNodes = {
      stop: () => {
        stopped = true;
        window.clearInterval(sweepTimer);
        window.clearInterval(pulseTimer);
        try {
          bus.gain.cancelScheduledValues(c.currentTime);
          bus.gain.linearRampToValueAtTime(0, c.currentTime + 0.4);
          [d1, d2, so, po].forEach((o) => o.stop(c.currentTime + 0.5));
        } catch {
          /* ignore */
        }
      },
    };
  },

  stopRansomAlarm() {
    ransomNodes?.stop();
    ransomNodes = null;
  },

  /** Ominous descending three-note sting for "the breach just completed" --
   *  played once the exfil progress bar hits 100%. */
  breachAlert() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    const now = c.currentTime;
    const notes = [880, 740, 600];
    notes.forEach((f, i) => {
      const t = now + i * 0.16;
      const o = c.createOscillator();
      o.type = "sawtooth";
      o.frequency.value = f;
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.15, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0004, t + 0.32);
      const lp = c.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 1400;
      o.connect(g).connect(lp).connect(master!);
      o.start(t);
      o.stop(t + 0.34);
    });
  },

  /** 8-bit arcade "laser fired" blip \u2014 a short, bright upward square-wave
   *  chirp. Used by the fake game-launcher cutscene (RetroInvadersGame) so
   *  the moment reads as a real, actively-playing arcade game. */
  retroShoot() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    const now = c.currentTime;
    const o = c.createOscillator();
    o.type = "square";
    o.frequency.setValueAtTime(1100, now);
    o.frequency.exponentialRampToValueAtTime(2200, now + 0.09);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(0.16, now + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 4000;
    o.connect(g).connect(lp).connect(master);
    o.start(now);
    o.stop(now + 0.11);
  },

  /** 8-bit arcade "invader hit / explosion" blip \u2014 a short descending
   *  noise-like square-wave burst. Pairs with retroShoot() in the fake
   *  game-launcher cutscene. */
  retroHit() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    const now = c.currentTime;
    const o = c.createOscillator();
    o.type = "square";
    o.frequency.setValueAtTime(420, now);
    o.frequency.exponentialRampToValueAtTime(70, now + 0.16);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(0.2, now + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 2200;
    o.connect(g).connect(lp).connect(master);
    o.start(now);
    o.stop(now + 0.19);
  },

  /** 8-bit arcade "game over" jingle \u2014 a short descending three-note
   *  square-wave phrase, played right as the fake game-launcher cutscene
   *  ends and the malware payload's flicker/crash sequence begins. */
  retroGameOver() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    const now = c.currentTime;
    const notes = [523.25, 415.3, 311.13];
    notes.forEach((f, i) => {
      const t = now + i * 0.15;
      const o = c.createOscillator();
      o.type = "square";
      o.frequency.value = f;
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.17, t + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0003, t + 0.32);
      const lp = c.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 2600;
      o.connect(g).connect(lp).connect(master!);
      o.start(t);
      o.stop(t + 0.34);
    });
  },

  /** Warm the cache for a line so it plays instantly when spoken.
   *  Returns a promise that resolves when the blob is ready (or on failure). */
  /** Set the active course id (used to locate its pre-rendered voice manifest).
   *  Called by App on scenario load. */
  setVoiceCourse(course: string): void {
    voiceCourse = course || "";
  },

  /** Set the effective content locale — the language the spoken text is in.
   *  Called by App on load and on locale change. */
  setVoiceLocale(locale: string): void {
    voiceLocale = locale || "en";
  },

  /** Load a course's pre-rendered voice manifest (hash -> static mp3 url).
   *  Best-effort: a missing manifest (course not yet pre-rendered) simply leaves
   *  every line on the live-TTS fallback. Cached per course. */
  async loadVoiceManifest(course: string = voiceCourse): Promise<void> {
    if (!course) return;
    if (voiceManifestCourse === course && voiceManifest.size) return;
    voiceManifestCourse = course;
    voiceManifest.clear();
    const run = (async () => {
      try {
        // /cdn/sim/* is served immutable; bust the cache so an updated manifest is
        // picked up on the next load. The mp3s it points at ARE content-hashed, so
        // they stay immutably cacheable.
        const url = `/cdn/sim/${encodeURIComponent(course)}/voice/manifest.json?v=${Date.now()}`;
        const res = await fetch(url, { cache: "no-store", credentials: "omit" });
        if (!res.ok) return;
        const data = (await res.json()) as Record<string, unknown>;
        for (const [k, v] of Object.entries(data)) if (typeof v === "string") voiceManifest.set(k, v);
      } catch {
        /* not pre-rendered yet / offline — live TTS fallback handles it */
      }
    })();
    voiceManifestReady = run;
    await run;
  },

  prefetchSpeech(text: string, opts?: SpeakOpts): Promise<void> {
    if (captureLine(text, opts)) return Promise.resolve();
    if (!enabled) return Promise.resolve();
    return fetchTts(text, opts).then(() => undefined).catch(() => undefined);
  },

  /**
   * Speak a line from the course's pre-rendered mp3. Never calls live TTS
   * (/api/tts, /sim/tts) or the browser speechSynthesis voice: both make the
   * start of a scene lag or switch to a robotic voice. A missing clip holds
   * the caption for a reading interval and continues.
   * Resolves when playback finishes. No-op (resolves) if sound is off.
   *
   * SINGLE-CHANNEL: a new speak() always stops any still-playing line first, so
   * scripted lines can never overlap into multiple voices at once.
   *
   * `opts.onStart` fires when audio is ABOUT TO play (after the TTS fetch). Use
   * it to reveal transcript/captions so text never appears 1–3s before the voice.
   * If sound is off, onStart still fires immediately so UI can advance.
   */
  async speak(text: string, opts?: SpeakOpts): Promise<void> {
    // Build-time enumeration: record the line and return immediately (no fetch,
    // no playback) so the capture driver walks the whole course fast. Inert in
    // production (the flag is never set there).
    if (captureLine(text, opts)) {
      try {
        opts?.onStart?.();
      } catch {
        /* ignore */
      }
      return;
    }
    // Caption lifecycle uses ITS OWN token, deliberately decoupled from
    // speechToken. speechToken gets bumped a SECOND time by stopSpeech()
    // below, so a token captured before that call is already stale by the
    // time playback starts — that exact bug once made every speak() bail out
    // of fireStart()/playUrl() early (silent audio, captions never cleared).
    const myCaption = ++captionToken;
    setCaption(text);
    if (!enabled || !voiceEnabled) {
      try {
        opts?.onStart?.();
      } catch {
        /* ignore */
      }
      // No audio is playing, but a hearing-impaired learner still needs time
      // to read the line before the caption clears.
      await new Promise<void>((r) => window.setTimeout(r, Math.min(6000, 1200 + text.length * 32)));
      if (myCaption === captionToken) setCaption(null);
      return;
    }
    stopSpeech();
    const myToken = ++speechToken;
    setSpeaking(true);
    // Duck the ambient bed for the duration of this line so narration is
    // always clearly audible over it (was: ambient played at full/fading
    // reference volume with no regard for whether a voice line was active,
    // which is why dramatic scenes in particular could bury the voice).
    duckAmbient(true);
    const fireStart = () => {
      if (myToken !== speechToken) return;
      try {
        opts?.onStart?.();
      } catch {
        /* ignore */
      }
    };
    try {
      const url = await fetchTts(text, opts);
      if (myToken !== speechToken) return; // superseded while fetching
      fireStart();
      await playUrl(url, myToken);
    } catch (err) {
      if (myToken !== speechToken) return;
      // eslint-disable-next-line no-console
      console.warn("[cf-audio] pre-rendered clip missing; not using live TTS:", err);
      fireStart();
      await new Promise<void>((r) => window.setTimeout(r, Math.min(6000, 1200 + text.length * 32)));
    } finally {
      if (myToken === speechToken) {
        setSpeaking(false);
        duckAmbient(false);
      }
      if (myCaption === captionToken) setCaption(null);
    }
  },

  cancelSpeech() {
    stopSpeech();
    captionToken++;
    setSpeaking(false);
    setCaption(null);
    duckAmbient(false);
  },

  isSpeaking() {
    return speaking;
  },

  /** Subscribe to speaking on/off. Returns an unsubscribe function. */
  subscribeSpeaking(fn: (v: boolean) => void) {
    speakingListeners.add(fn);
    return () => {
      speakingListeners.delete(fn);
    };
  },

  /** Current caption text (null when nothing is being said). */
  getCaption() {
    return caption;
  },

  /** Playback position of the current spoken line, for the in-slide narration strip. */
  speechProgress() {
    if (!caption) return null;
    const el = speechEl;
    const playing = voiceEnabled && !!caption;
    const estimate = Math.max(0.1, captionReadMs / 1000);
    if (el) {
      const duration = Number.isFinite(el.duration) && el.duration > 0 && el.duration !== Infinity ? el.duration : estimate;
      return { current: Math.min(el.currentTime || 0, duration), duration, playing: playing && !el.ended };
    }
    const elapsed = (performance.now() - captionStarted) / 1000;
    return { current: Math.min(elapsed, estimate), duration: estimate, playing };
  },

  /** Subscribe to the accessibility caption text. Returns an unsubscribe fn. */
  subscribeCaption(fn: (text: string | null) => void) {
    captionListeners.add(fn);
    return () => {
      captionListeners.delete(fn);
    };
  },

  /** A short descending "call ended" tone — play when the learner (or the
   *  other party) hangs up. Synthesized, no asset. */
  callHangup() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    const now = c.currentTime;
    const o = c.createOscillator();
    o.type = "sine";
    o.frequency.setValueAtTime(520, now);
    o.frequency.exponentialRampToValueAtTime(200, now + 0.28);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(0.16, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 1200;
    o.connect(g).connect(lp).connect(master);
    o.start(now);
    o.stop(now + 0.34);
  },

  /** A soft rising whoosh + settle chime for moving between zones/scenes
   *  (elevator doors, a hard camera cut to a new area). Synthesized, no
   *  asset. Distinct from click()/notification() — this reads as "the
   *  world just changed", not a UI action. */
  sceneTransition() {
    if (!enabled) return;
    const c = ensureCtx();
    if (!master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    const now = c.currentTime;
    const dur = 0.5;
    const buf = c.createBuffer(1, Math.ceil(c.sampleRate * dur), c.sampleRate);
    const d = buf.getChannelData(0);
    for (let j = 0; j < d.length; j++) d[j] = (Math.random() * 2 - 1) * Math.sin((j / d.length) * Math.PI);
    const src = c.createBufferSource();
    src.buffer = buf;
    const bp = c.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.setValueAtTime(500, now);
    bp.frequency.linearRampToValueAtTime(2400, now + dur);
    bp.Q.value = 0.8;
    const g = c.createGain();
    g.gain.value = 0.05;
    src.connect(bp).connect(g).connect(master);
    src.start(now);
    src.stop(now + dur);
    const o = c.createOscillator();
    o.type = "sine";
    o.frequency.value = 1318.5;
    const g2 = c.createGain();
    g2.gain.setValueAtTime(0.0001, now + dur * 0.7);
    g2.gain.linearRampToValueAtTime(0.1, now + dur * 0.7 + 0.03);
    g2.gain.exponentialRampToValueAtTime(0.0001, now + dur + 0.25);
    o.connect(g2).connect(master);
    o.start(now + dur * 0.7);
    o.stop(now + dur + 0.3);
  },
};

interface SpeakOpts {
  lang?: string;
  voice?: string;
  instructions?: string;
  /** Explicit pre-rendered audio URL. When set, that clip is played directly
   *  (no live TTS fetch). Normally left unset — the per-course voice manifest
   *  resolves pre-rendered clips automatically by content hash. */
  audioUrl?: string;
  /** Called when playback is about to start (after TTS is ready). */
  onStart?: () => void;
}

/**
 * Fetch a pre-rendered clip for a line. Waits for the course manifest, then
 * plays the static mp3. Never calls live TTS. Concurrent prefetch+speak for
 * the same line share one fetch.
 */
async function fetchTts(text: string, opts?: SpeakOpts): Promise<string> {
  // The manifest fetch is in flight on the same tick a scene's first line
  // speaks. Wait for it before deciding the clip is missing.
  await voiceManifestReady;
  // Cache/lookup key is the FULL tuple hash (voice + instructions + lang +
  // text) — the old `voice|text` key silently collided lines that differed
  // only by instructions or language.
  const { hash } = ttsKey(text, opts);
  const hit = ttsBlobCache.get(hash);
  if (hit) return hit;
  // Pre-rendered only. An explicit opts.audioUrl, else the per-course manifest.
  // No live /api/tts and no /sim/tts: that round trip is the lag at scene start.
  const staticUrl = opts?.audioUrl || voiceManifest.get(hash);
  if (!staticUrl) throw new Error("no prerendered clip " + hash);
  const promise = (async () => {
    const r = await fetch(staticUrl, { credentials: "omit" });
    if (!r.ok) throw new Error("prerendered clip " + r.status + " " + staticUrl);
    return URL.createObjectURL(await r.blob());
  })();
  ttsBlobCache.set(hash, promise);
  promise.catch(() => {
    // Don't poison the cache with a failed attempt — a later retry (e.g. the
    // next time this same line is spoken) should get a fresh shot.
    if (ttsBlobCache.get(hash) === promise) ttsBlobCache.delete(hash);
  });
  if (ttsBlobCache.size > 40) {
    const firstKey = ttsBlobCache.keys().next().value;
    if (firstKey && firstKey !== hash) ttsBlobCache.delete(firstKey);
  }
  return promise;
}

type LiveSpeechEl = HTMLAudioElement & { __cfFinish?: () => void; __cfDead?: boolean };

let lastSpeechEl: LiveSpeechEl | null = null;
/** Clips that must still be stoppable. Natural end removes them; a spurious
 *  "ended" while audio is still audible does not. */
const speechKeep = new Set<LiveSpeechEl>();

function silenceEl(el: LiveSpeechEl) {
  try {
    el.pause();
    el.currentTime = 0;
    el.removeAttribute("src");
    el.load();
  } catch {
    /* ignore */
  }
}

/** Hard-stop one scripted clip. A later play() resolution must not revive it. */
function killSpeechEl(el: LiveSpeechEl | null) {
  if (!el || el.__cfDead) {
    if (el) silenceEl(el);
    return;
  }
  el.__cfDead = true;
  const finish = el.__cfFinish;
  el.__cfFinish = undefined;
  el.onended = null;
  el.onerror = null;
  liveSpeech.delete(el);
  speechKeep.delete(el);
  if (speechEl === el) speechEl = null;
  if (lastSpeechEl === el) lastSpeechEl = null;
  silenceEl(el);
  finish?.();
}

/** Stop every scripted clip still alive (server TTS audio + browser TTS). */
function stopSpeech() {
  speechToken++;
  try {
    window.speechSynthesis?.cancel();
  } catch {
    /* ignore */
  }
  const doomed = new Set<LiveSpeechEl>([...liveSpeech, ...speechKeep]);
  if (lastSpeechEl) doomed.add(lastSpeechEl);
  if (speechEl) doomed.add(speechEl as LiveSpeechEl);
  speechEl = null;
  lastSpeechEl = null;
  for (const el of doomed) killSpeechEl(el);
}

function playUrl(url: string, token: number): Promise<void> {
  return new Promise((resolve) => {
    if (token !== speechToken) {
      resolve();
      return;
    }
    const el = new Audio(url) as LiveSpeechEl;
    let settled = false;
    const finish = () => {
      // Never drop a clip that is still making sound. The next line has to
      // be able to stop it.
      if (!el.__cfDead && !el.ended && !el.paused) return;
      if (settled) return;
      settled = true;
      el.__cfFinish = undefined;
      liveSpeech.delete(el);
      speechKeep.delete(el);
      if (speechEl === el) speechEl = null;
      if (lastSpeechEl === el) lastSpeechEl = null;
      resolve();
    };
    el.__cfFinish = finish;
    liveSpeech.add(el);
    speechKeep.add(el);
    speechEl = el;
    lastSpeechEl = el;
    el.onended = () => finish();
    const started = el.play();
    void started.then(() => {
      if (el.__cfDead || token !== speechToken) silenceEl(el);
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error("[cf-audio] speech play() rejected:", err?.name, err?.message);
      killSpeechEl(el);
    });
    if (token !== speechToken || el.__cfDead) {
      killSpeechEl(el);
      return;
    }
    let lastTime = -1;
    let still = 0;
    const watch = window.setInterval(() => {
      if (settled || el.__cfDead || token !== speechToken) {
        window.clearInterval(watch);
        return;
      }
      if (el.ended) {
        window.clearInterval(watch);
        finish();
        return;
      }
      if (!el.paused && el.currentTime > lastTime + 0.05) {
        lastTime = el.currentTime;
        still = 0;
        return;
      }
      still += 1;
      // Only unblock a clip that has actually stalled. A long line must play out.
      if (still < 8) return;
      window.clearInterval(watch);
      resolve();
    }, 2000);
  });
}
